import { useMemo } from 'react'
import {
  useGetPlantLineInventoryQuery,
  type PlantLineInventoryItem
} from '@axilon-apps/domains-ontology-ui-state'

import { LANDING_TASK_CARDS, type LandingTaskCard } from './chat-landing'

export type SuggestedLine = Pick<PlantLineInventoryItem, 'id' | 'name' | 'equipmentCount'>

/** Diagnose, History, and Build each get a Line; Alarms stays generic. */
export const NAMED_PROMPT_COUNT = 3

export function hasUsableLineName(name: string, id: string): boolean {
  const trimmed = name.trim()
  if (!trimmed) return false
  if (/^urn:/i.test(trimmed)) return false
  if (trimmed === id && /^(line|urn)[:/]/i.test(id)) return false
  return true
}

function displayNameKey(name: string): string {
  return name.trim().toLowerCase()
}

export function usableSuggestedLines(
  lines: readonly SuggestedLine[] | null | undefined
): SuggestedLine[] {
  if (!lines || lines.length === 0) return []
  const seen = new Set<string>()
  const usable: SuggestedLine[] = []
  for (const line of lines) {
    if (!hasUsableLineName(line.name, line.id)) continue
    const key = displayNameKey(line.name)
    if (seen.has(key)) continue
    seen.add(key)
    usable.push(line)
  }
  return usable
}

function shuffleInPlace<T>(items: T[], random: () => number): T[] {
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1))
    const current = items[i]
    items[i] = items[j]
    items[j] = current
  }
  return items
}

/**
 * Distinct displayable Lines for L2 prompts. Random among the usable
 * inventory; fewer than `count` returns every usable Line.
 */
export function pickSuggestedLines(
  lines: readonly SuggestedLine[] | null | undefined,
  count = NAMED_PROMPT_COUNT,
  random: () => number = Math.random
): SuggestedLine[] {
  const usable = usableSuggestedLines(lines)
  if (usable.length === 0) return []
  return shuffleInPlace([...usable], random).slice(0, Math.min(count, usable.length))
}

export function plantAwareLandingCards(lineNames: readonly string[]): LandingTaskCard[] {
  const names = lineNames.map((name) => name.trim()).filter((name) => name.length > 0)
  const diagnose = names[0]
  const history = names[1] ?? names[0]
  const build = names[2] ?? names[1] ?? names[0]
  if (!diagnose || !history || !build) return LANDING_TASK_CARDS
  return [
    { id: 'diagnose', label: 'Diagnose', prompt: `What's the status of ${diagnose}?`, mode: 'ask' },
    { id: 'alarms', label: 'Alarms', prompt: "What's alarming right now?", mode: 'ask' },
    { id: 'history', label: 'History', prompt: `What do we know about ${history}?`, mode: 'ask' },
    { id: 'build', label: 'Build', prompt: `Make me a dashboard for ${build}`, mode: 'agent' }
  ]
}

/**
 * L1 generic cards when there is no usable Line; L2 named cards when
 * inventory includes at least one displayable Line. Diagnose, History,
 * and Build each get a distinct random Line when three exist.
 */
export function selectSuggestedPrompts(
  lines: readonly SuggestedLine[] | null | undefined,
  random: () => number = Math.random
): LandingTaskCard[] {
  const picked = pickSuggestedLines(lines, NAMED_PROMPT_COUNT, random)
  if (picked.length === 0) return LANDING_TASK_CARDS
  return plantAwareLandingCards(picked.map((line) => line.name))
}

export function useSuggestedChatPrompts(options?: { skip?: boolean }): LandingTaskCard[] {
  const skip = options?.skip === true
  const { data, isError, isLoading } = useGetPlantLineInventoryQuery(undefined, { skip })
  return useMemo(() => {
    if (skip || isError || isLoading) return selectSuggestedPrompts(null)
    return selectSuggestedPrompts(data ?? [])
  }, [data, isError, isLoading, skip])
}
